import axios from 'axios';
import { variables } from '../config/Variables';

// Uygulama versiyonu – modül yüklenirken bir kez okunur
let appVersion = '';
fetch('/version.json')
    .then(r => r.json())
    .then(data => { appVersion = data.version || ''; })
    .catch(() => {});

// Refresh token işlemi sürerken gelen 401'leri bekletmek için kuyruk
let isRefreshing = false;
let refreshQueue = []; // [{ resolve, reject }]

const setIsRefreshing = (value) => {
    isRefreshing = value;
    window.dispatchEvent(new CustomEvent('api-refresh-state', { detail: value }));
};

const processQueue = (error, token = null) => {
    refreshQueue.forEach(({ resolve, reject }) => {
        error ? reject(error) : resolve(token);
    });
    refreshQueue = [];
};

const clearSession = () => {
    sessionStorage.clear();
    document.cookie = 'kama3d_session=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Strict';
};

// Axios instance oluşturma
const api = axios.create({
    baseURL: variables.API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request Interceptor: Her isteğe Token ve uygulama versiyonu ekle
api.interceptors.request.use(
    (config) => {
        const token = sessionStorage.getItem("jwtKey");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        if (appVersion) {
            config.headers['X-App-Version'] = appVersion;
        }
        // Send the full current page URL to bypass cross-origin referrer stripping in browsers
        config.headers['X-Original-Referer'] = window.location.href;
        return config;
    },
    (error) => Promise.reject(error)
);

// Response Interceptor: 401'de refresh token dene, başarısızsa logout
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status !== 401) {
            return Promise.reject(error);
        }

        // Login/RefreshToken endpoint'inden 401 gelirse döngüye girme
        if (originalRequest.url?.toLowerCase().includes('/login')) {
            return Promise.reject(error);
        }

        // Bu istek zaten bir kez refresh denemiş, yine 401 → çıkış
        if (originalRequest._retry) {
            clearSession();
            window.location.href = '/login';
            return Promise.reject(error);
        }

        const storedRefreshToken = sessionStorage.getItem("refreshToken");
        if (!storedRefreshToken) {
            // Refresh token yoksa direkt çıkış
            clearSession();
            window.location.href = '/login';
            return Promise.reject(error);
        }

        // Refresh zaten devam ediyorsa bu isteği kuyruğa al
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                refreshQueue.push({ resolve, reject });
            }).then(newToken => {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
            }).catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        setIsRefreshing(true);

        try {
            // Refresh isteği: interceptor döngüsünü önlemek için ham axios kullanılır
            const refreshUrl = variables.API_URL.replace(/\/+$/, '') + '/Login/RefreshToken';
            const resp = await axios.post(
                refreshUrl,
                JSON.stringify(storedRefreshToken),
                { headers: { 'Content-Type': 'application/json' } }
            );

            const { jwtKey, refreshToken: newRefreshToken, activationKey, eposta, userId } = resp.data;

            // Yeni token'ları sakla
            sessionStorage.setItem("jwtKey", jwtKey);
            sessionStorage.setItem("refreshToken", newRefreshToken);
            if (activationKey) sessionStorage.setItem("activationKey", activationKey);
            if (eposta) {
                sessionStorage.setItem("eposta", eposta);
                sessionStorage.setItem("ePosta", eposta);
                localStorage.setItem("eposta", eposta);
            }
            if (userId) sessionStorage.setItem("userId", userId);

            // Broadcast new auth data to all active iframes (3D Catalog etc.)
            const iframes = document.getElementsByTagName('iframe');
            for (let i = 0; i < iframes.length; i++) {
                try {
                    iframes[i].contentWindow.postMessage(
                        { type: 'SET_USER_ID', payload: userId, token: jwtKey },
                        '*'
                    );
                } catch (e) { /* cross-origin or other issues */ }
            }

            // "Beni Hatırla" cookie'si varsa güncelle
            const cookieMatch = document.cookie.match(/(^| )kama3d_session=([^;]+)/);
            if (cookieMatch) {
                try {
                    const sessionData = JSON.parse(decodeURIComponent(cookieMatch[2]));
                    sessionData.jwtKey = jwtKey;
                    sessionData.refreshToken = newRefreshToken;
                    if (activationKey) sessionData.activationKey = activationKey;
                    if (eposta) sessionData.eposta = eposta;
                    if (userId) sessionData.userId = userId;
                    const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
                    document.cookie = `kama3d_session=${encodeURIComponent(JSON.stringify(sessionData))};expires=${expires};path=/;SameSite=Strict`;
                } catch (_) { /* cookie parse hatası – önemli değil */ }
            }

            // Bekleyen istekleri serbest bırak
            processQueue(null, jwtKey);

            // Orijinal isteği yeni token ile tekrar gönder
            originalRequest.headers.Authorization = `Bearer ${jwtKey}`;
            return api(originalRequest);

        } catch (refreshError) {
            processQueue(refreshError, null);
            clearSession();
            window.location.href = '/login';
            return Promise.reject(refreshError);
        } finally {
            setIsRefreshing(false);
        }
    }
);

export default api;
